import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  Select,
  Grid,
  NumberInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { notifications } from "@mantine/notifications";
import {
  getAllOperationItemsFromPriceGroup,
  createWorkItem,
  updateWorkItem,
} from "../../../../services/objectmodelservice";
import { WorkPhaseItemDto } from "../../../../types/WorkPhaseItem";
import { WorkStep, WorkStepForm } from "../../types/ObjectFormTypes";
import { WorkPhaseItemDto as FetchedItem } from "../../../../types/WorkPhaseItem";
import { useTranslation } from "../../../../hooks/useTranslation";
import useTransientStore from "../../../../data/transcientStore";

interface AddEditWorkStepModalProps {
  opened: boolean;
  onClose: () => void;
  editingWorkStep: WorkStep | null;
  onSave: (values: WorkStepForm) => void;
  contractId?: number; // for dynamic operation loading
  objectId?: number; // for dynamic operation loading
  workItemType?: number; // optional work item type
  editingFetchedItem?: FetchedItem | null; // to prefill description/unit price precisely
  // When editing an 'Others' item, force operation to Other and prefill fields
  isOtherOperation?: boolean;
}

// moved to top to satisfy linting import order

export default function AddEditWorkStepModal({
  opened,
  onClose,
  editingWorkStep,
  onSave,
  contractId,
  objectId,
  workItemType = 0,
  editingFetchedItem = null,
  isOtherOperation = false,
}: AddEditWorkStepModalProps) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 305,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });
  const form = useForm<WorkStepForm>({
    initialValues: {
      operation: "",
      amount: 0,
      surfaceArea: 0,
      tons: 0,
      anotherName: "",
      otherUnit: "",
      otherUnitPrice: 0,
      description: "",
    },
    validate: {
      operation: (value: string) =>
        !value.trim()
          ? getTranslation("LABEL_WORKSTEP_OPERATION_REQUIRED") ||
            "Operation is required"
          : null,
      amount: (value: number) =>
        value <= 0
          ? getTranslation("LABEL_WORKSTEP_AMOUNT_REQUIRED") ||
            "Amount is required and must be greater than 0"
          : null,
      anotherName: (value: string, values) =>
        values.operation === "-1" && !value.trim()
          ? getTranslation("LABEL_WORKSTEP_NAME_REQUIRED") || "Name is required"
          : null,
      otherUnit: (value: string, values) =>
        values.operation === "-1" && !value.trim()
          ? getTranslation("LABEL_WORKSTEP_UNIT_REQUIRED") || "Unit is required"
          : null,
      otherUnitPrice: (value: number | undefined, values) =>
        values.operation === "-1" && (!value || value <= 0)
          ? getTranslation("LABEL_WORKSTEP_UNIT_PRICE_REQUIRED") ||
            "Unit Price is required and must be greater than 0"
          : null,
    },
  });
  const isOtherSelected = form.values.operation === "-1";

  // Update form when editing work step changes
  useEffect(() => {
    if (editingWorkStep) {
      const operationValue = isOtherOperation
        ? "-1"
        : editingFetchedItem?.priceItemId && editingFetchedItem.priceItemId > 0
        ? String(editingFetchedItem.priceItemId)
        : "-1";
      form.setValues({
        operation: operationValue,
        amount: editingWorkStep.amount,
        surfaceArea: editingWorkStep.surfaceArea,
        tons: editingWorkStep.tons,
        anotherName: isOtherOperation ? editingFetchedItem?.name ?? "" : "",
        otherUnit: isOtherOperation
          ? editingFetchedItem?.unitName ?? ""
          : editingWorkStep.unit,
        otherUnitPrice: isOtherOperation
          ? editingFetchedItem?.unitPrice ?? 0
          : editingFetchedItem?.unitPrice ?? editingWorkStep.unitPrice ?? 0,
        description: editingFetchedItem?.description ?? "",
      });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingWorkStep, editingFetchedItem, opened, isOtherOperation]);

  const handleSubmit = async (values: WorkStepForm) => {
    // Map selected operation value to priceItemId ("-1" => Other/null, group-* => header ignored)
    let priceItemId: number | null = null;
    if (
      values.operation &&
      !values.operation.startsWith("group-") &&
      values.operation !== "-1"
    ) {
      const parsed = parseInt(values.operation, 10);
      if (!isNaN(parsed)) priceItemId = parsed;
    }
    try {
      if (contractId && objectId) {
        // If editing an existing fetched item, update it; otherwise create new
        if (editingWorkStep && editingWorkStep.id && editingWorkStep.id > 0) {
          const updatePayload: any = {
            workItemId: editingWorkStep.id,
            priceItemId,
            itemCount: values.amount,
            description: values.description || undefined,
            workItemType: workItemType ?? 0,
            itemArea: values.surfaceArea || undefined,
            itemWeight: values.tons || undefined,
            unitPrice:
              unitPriceByValue[values.operation] ??
              values.otherUnitPrice ??
              undefined,
          };
          if (!priceItemId) {
            updatePayload.itemName = values.anotherName || undefined;
            updatePayload.unitName = values.otherUnit || undefined;
          }
          await updateWorkItem(editingWorkStep.id, updatePayload);
        } else {
          const payload: any = {
            priceItemId,
            contractId,
            objectId,
            itemCount: values.amount,
            description: values.description || undefined,
            workItemType: workItemType ?? 0,
            itemArea: values.surfaceArea || undefined,
            itemWeight: values.tons || undefined,
            unitPrice:
              unitPriceByValue[values.operation] ??
              values.otherUnitPrice ??
              undefined,
          };
          // If Other is selected (no price item), include itemName and unitName
          if (!priceItemId) {
            payload.itemName = values.anotherName || undefined;
            payload.unitName = values.otherUnit || undefined;
          }
          await createWorkItem(payload);
        }
      }
      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || "Success",
        message:
          getTranslation("MESSAGE_WORK_ITEM_SAVED") ||
          "Work item saved successfully.",
        color: "green",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-success",
        },
      });
      onSave(values); // propagate to parent UI state
      form.reset();
    } catch (error) {
      console.error("Failed to create work item", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message:
          getTranslation("MESSAGE_WORK_ITEM_SAVE_FAILED") ||
          `Failed to save work item: ${errorMessage}`,
        color: "red",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-error",
        },
      });
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  // Dynamic operation options fetched from backend endpoint
  const [operationOptions, setOperationOptions] = useState<
    { value: string; label: string; disabled?: boolean }[]
  >([]);
  const [unitPriceByValue, setUnitPriceByValue] = useState<
    Record<string, number>
  >({});
  const [isLoadingOperations, setIsLoadingOperations] = useState(false);

  useEffect(() => {
    const fetchOperations = async () => {
      if (!contractId || !objectId) {
        // No identifiers → no operations; leave empty to avoid showing options
        setOperationOptions([]);
        return;
      }
      setIsLoadingOperations(true);
      try {
        const items: WorkPhaseItemDto[] =
          await getAllOperationItemsFromPriceGroup(
            contractId,
            objectId,
            workItemType
          );
        // Build options including group headers (workItemId === 0) as disabled entries
        let options: { value: string; label: string; disabled?: boolean }[] =
          [];
        let unitPriceMap: Record<string, number> = {};
        const seenLeafNames = new Set<string>();
        for (const it of items) {
          const cleanName = it.name.replace(/\u00A0+/g, " ").trim();
          if (it.workItemId === 0) {
            // Group header: disabled option
            options.push({
              value: `group-${it.orderInGroup}`,
              label: cleanName,
              disabled: true,
            });
          } else {
            if (!seenLeafNames.has(cleanName)) {
              seenLeafNames.add(cleanName);
              const valueStr = String(it.workItemId);
              // Indent leaf items visually under headers using non-breaking spaces
              const indentedLabel = `\u00A0\u00A0\u00A0${cleanName}`;
              options.push({ value: valueStr, label: indentedLabel });
              if (typeof it.unitPrice === "number") {
                unitPriceMap[valueStr] = it.unitPrice;
              }
            }
          }
        }
        // Append selectable "Other" option if not already present
        options.push({
          value: "-1",
          label: getTranslation("LABEL_OTHER") || "Other",
        });
        // Ensure the currently editing price item exists in options, if any.
        // Note: the API excludes items already used in WorkItems, so we must inject it.
        if (
          editingFetchedItem &&
          editingFetchedItem.priceItemId &&
          editingFetchedItem.priceItemId > 0
        ) {
          const val = String(editingFetchedItem.priceItemId);
          const exists = options.some((o) => o.value === val);
          if (!exists) {
            const cleanName =
              editingFetchedItem.name?.replace(/\u00A0+/g, " ").trim() || "";
            const unit = editingFetchedItem.unitName || "";
            const label = `\u00A0\u00A0\u00A0${cleanName}${
              unit ? ` (${unit})` : ""
            }`;
            options = [...options, { value: val, label }];
            unitPriceMap[val] = editingFetchedItem.unitPrice ?? 0;
          }
          if (typeof editingFetchedItem.description === "string") {
            form.setFieldValue("description", editingFetchedItem.description);
          }
        }
        setOperationOptions(options);
        setUnitPriceByValue(unitPriceMap);
      } catch (err) {
        console.error("Failed to load operations", err);
        // On error, also leave options empty to avoid misleading choices
        setOperationOptions([]);
        setUnitPriceByValue({});
      } finally {
        setIsLoadingOperations(false);
      }
    };
    if (opened) {
      fetchOperations();
    }
  }, [opened, contractId, objectId, workItemType]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        editingWorkStep
          ? getTranslation("TITLE_EDIT_PLANNED_WORK_STEP") ||
            "Edit planned work step"
          : getTranslation("TITLE_ADD_PLANNED_WORK_STEP") ||
            "Add planned work step"
      }
      size="lg"
      radius="md"
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
      lockScroll={true}
      styles={{
        body: {
          position: "relative",
          minHeight: "400px",
          paddingTop: "16px",
        },
        content: { position: "relative" },
        header: {
          paddingRight: "0px",
          background: "linear-gradient(to bottom, #f0f0f0 0%, #e5e5e5 100%)",
          borderBottom: "1px solid #d0d0d0",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
          minHeight: "40px",
          padding: "8px 16px",
        },
        close: { marginLeft: "0px", marginRight: "12px" },
        title: {
          width: "100%",
          flex: "1",
          fontWeight: "bold",
          color: "#1976d2",
        },
      }}
    >
      {/* Local CSS to bold disabled (header) items */}
      <style>{`.workstep-select .mantine-Select-item[data-disabled]{font-weight:600;}`}</style>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Select
            label={getTranslation("LABEL_OPERATION") || "Operation"}
            placeholder={
              isLoadingOperations
                ? getTranslation("LABEL_LOADING_OPERATIONS") ||
                  "Loading operations..."
                : getTranslation("PLACEHOLDER_SELECT_OPERATION") ||
                  "Select operation"
            }
            data={operationOptions}
            required
            // searchable
            disabled={isLoadingOperations}
            className="workstep-select"
            {...form.getInputProps("operation")}
            onChange={(val) => {
              form.setFieldValue("operation", val || "");
              if (val && unitPriceByValue[val] != null) {
                form.setFieldValue("otherUnitPrice", unitPriceByValue[val]);
              } else if (val === "-1") {
                // Other selected: do not auto-fill unit price; leave or reset to 0
                form.setFieldValue("otherUnitPrice", 0);
                // Ensure dependent fields are cleared and validated
                form.setFieldValue("anotherName", "");
                form.setFieldValue("otherUnit", "");
              } else {
                // Clear if not found
                form.setFieldValue("otherUnitPrice", 0);
                form.setFieldValue("anotherName", "");
                form.setFieldValue("otherUnit", editingWorkStep?.unit || "");
              }
            }}
          />

          <Grid>
            <Grid.Col span={6}>
              <NumberInput
                label={getTranslation("LABEL_AMOUNT") || "Amount"}
                placeholder={getTranslation("PLACEHOLDER_ENTER_AMOUNT") || "Enter amount"}
                required
                min={0}
                step={0.1}
                decimalScale={2}
                hideControls
                {...form.getInputProps("amount")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label={getTranslation("LABEL_SURFACE_AREA") || "Surface Area"}
                placeholder={getTranslation("PLACEHOLDER_ENTER_SURFACE_AREA") || "Enter surface area"}
                min={0}
                step={0.1}
                decimalScale={2}
                hideControls
                {...form.getInputProps("surfaceArea")}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <NumberInput
                label={getTranslation("LABEL_TONS") || "Tons"}
                placeholder={getTranslation("PLACEHOLDER_ENTER_TONS") || "Enter tons"}
                min={0}
                step={0.1}
                decimalScale={2}
                hideControls
                {...form.getInputProps("tons")}
              />
            </Grid.Col>
            {isOtherSelected && (
              <>
                <Grid.Col span={6}>
                  <TextInput
                    label={getTranslation("LABEL_ANOTHER_NAME") || "Another Name"}
                    placeholder={getTranslation("PLACEHOLDER_ENTER_ADDITIONAL_NAMEIDENTIFIER") || "Enter additional name/identifier"}
                    withAsterisk
                    {...form.getInputProps("anotherName")}
                  />
                </Grid.Col>
              </>
            )}
          </Grid>

          {isOtherSelected && (
            <>
              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label={getTranslation("LABEL_OTHER_UNIT") || "Other Unit"}
                    placeholder={getTranslation("PLACEHOLDER_ENTER_UNIT_EG_M_HRS_KG") || "Enter unit (e.g., m², hrs, kg)"}
                    withAsterisk
                    {...form.getInputProps("otherUnit")}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <NumberInput
                    label={getTranslation("LABEL_OTHER_UNIT_PRICE") || "Other Unit Price"}
                    placeholder={getTranslation("PLACEHOLDER_ENTER_OTHER_UNIT_PRICE") || "Enter Other Unit Price"}
                    min={0}
                    step={0.1}
                    decimalScale={2}
                    hideControls
                    withAsterisk
                    {...form.getInputProps("otherUnitPrice")}
                  />
                </Grid.Col>
              </Grid>
            </>
          )}
          <Textarea
            label={getTranslation("LABEL_DESCRIPTION") || "Description"}
            placeholder={getTranslation("PLACEHOLDER_ENTER_DETAILED_DESCRIPTION") || "Enter detailed description"}
            minRows={3}
            maxRows={6}
            autosize
            {...form.getInputProps("description")}
          />

          <Group justify="flex-end">
            <Button variant="light" onClick={handleClose}>
              {getTranslation("LABEL_CANCEL") || "Cancel"}
            </Button>
            <Button type="submit">
              {editingWorkStep
                ? getTranslation("LABEL_UPDATE") || "Update"
                : getTranslation("LABEL_SAVE") || "Save"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
